import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ImportSummary, type Message, type Trunk } from '../api.js';
import { COUNTRIES, LANGUAGES, countryForPrefix, langForCountry,
         prefixForCountry, prefixMismatch } from '@tmt/shared';
import Icon from '../components/Icon.js';

/** The same form serves creation and editing — the fields are identical, and
 *  two copies of them would diverge at the first change. */
export default function CampaignNew() {
  const nav = useNavigate();
  const { id } = useParams();
  const editId = id ? Number(id) : null;
  const [trunks, setTrunks] = useState<Trunk[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [imported, setImported] = useState<ImportSummary | null>(null);

  const [f, setF] = useState({
    name: '', country: 'GB', lang: 'en-GB', allowedPrefix: '44', cli: '',
    trunkId: 0, ttsMessageId: 0, concurrency: 5, callsPerMin: 30, ringTimeout: 45,
    costPerMin: '0',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const v = e.target.value;
    // The country drags the prefix AND the default language along with it —
    // three fields that must stay coherent, otherwise the batch is rejected at
    // import. The prefix stays editable, but a silent mismatch can no longer
    // survive unnoticed.
    if (k === 'country') {
      setF({ ...f, country: v, allowedPrefix: prefixForCountry(v) || f.allowedPrefix,
             lang: langForCountry(v) || f.lang });
      return;
    }
    // The reverse path: you type the prefix, the country aligns. Nothing changes
    // if the current country already has this prefix — otherwise US would keep
    // jumping to CA.
    if (k === 'allowedPrefix') {
      const c = countryForPrefix(v);
      const keep = prefixForCountry(f.country) === v.replace(/^\+/, '').trim();
      setF({ ...f, allowedPrefix: v,
             ...(c && !keep ? { country: c.code, lang: c.lang } : {}) });
      return;
    }
    setF({ ...f, [k]: v });
  };

  useEffect(() => {
    void api.trunks().then((t) => {
      setTrunks(t);
      // The defaults are applied ONLY on creation. When editing they would
      // overwrite the already chosen trunk and CLI, depending on which request
      // answers first.
      if (!editId && t[0]) setF((prev) => ({ ...prev, trunkId: t[0]!.id, cli: t[0]!.dids[0] ?? '' }));
    });
    void api.messages().then(setMessages);

    if (editId) {
      void api.campaign(editId).then((c) => setF({
        name: c.name, country: c.country, lang: c.lang,
        allowedPrefix: c.allowed_prefix, cli: c.cli,
        trunkId: c.trunk_id, ttsMessageId: c.tts_message_id,
        concurrency: c.concurrency, callsPerMin: c.calls_per_min,
        ringTimeout: c.ring_timeout, costPerMin: String(c.cost_per_min ?? 0),
      })).catch((e) => setErr(e.message));
    }
  }, [editId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const payload = {
        ...f, trunkId: Number(f.trunkId), ttsMessageId: Number(f.ttsMessageId),
        concurrency: Number(f.concurrency), callsPerMin: Number(f.callsPerMin),
        ringTimeout: Number(f.ringTimeout), costPerMin: Number(f.costPerMin),
      };
      if (editId) {
        await api.updateCampaign(editId, payload);
        nav(`/campaigns/${editId}`);
        return;
      }
      const c = await api.createCampaign(payload);
      if (!file) { nav(`/campaigns/${c.id}`); return; }

      const summary = await api.uploadNumbers(c.id, file);
      setImported(summary);
      // If the whole batch was rejected, we do not take the operator into a
      // campaign that looks ready but has nothing to dial — we show them why, here.
      const accepted = summary.summary['accepted'] ?? 0;
      if (accepted > 0) nav(`/campaigns/${c.id}`);
    } catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
    finally { setBusy(false); }
  }

  const trunk = trunks.find((t) => t.id === Number(f.trunkId));

  return (
    <>
      <h1>{editId ? `Edit campaign #${editId}` : 'New campaign'}</h1>
      <p className="subtitle">
        {editId
          ? 'Changes do not affect numbers already dialled. The campaign must be stopped.'
          : 'Numbers pass through guards at import: prefix, suppression, duplicates.'}
      </p>
      {err && <div className="err">{err}</div>}

      {imported && (
        <div className={(imported.summary['accepted'] ?? 0) > 0 ? 'card' : 'err'}
             style={{ marginBottom: 16 }}>
          <strong>{imported.summary['accepted'] ?? 0} numbers accepted</strong> out of {imported.read} read.
          {Object.entries(imported.summary).filter(([k]) => k !== 'accepted').map(([k, v]) => (
            <span key={k}> · {v} rejected ({k})</span>
          ))}
          {(imported.summary['prefix'] ?? 0) > 0 && (
            <div style={{ marginTop: 6 }}>
              The numbers rejected for <code>prefix</code> do not start with <code>{f.allowedPrefix}</code>.
              Change the campaign country or prefix, then upload again.
            </div>
          )}
        </div>
      )}

      <form onSubmit={submit} className="card" style={{ maxWidth: 780 }}>
        <div className="grid2">
          <label><span>Name</span><input value={f.name} onChange={set('name')} required /></label>
          <label><span>Country</span>
            <select value={f.country} onChange={set('country')} required>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name} (+{c.prefix})</option>
              ))}
            </select></label>
        </div>

        <div className="grid2">
          <label><span>Message language</span>
            <select value={f.lang} onChange={set('lang')} required>
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select></label>
          <label><span>Allowed prefix — cost guard</span>
            <input value={f.allowedPrefix} onChange={set('allowedPrefix')} required /></label>
        </div>
        <p className="muted" style={{ marginTop: -6, fontSize: 12 }}>
          The language is deliberately separate from the country: Belgium is nl+fr,
          Switzerland de+fr+it. The prefix stops calls to other destinations if the
          file contains the wrong numbers.
        </p>
        {prefixMismatch(f.country, f.allowedPrefix) && (
          <div className="err" style={{ marginTop: 4 }}>
            The prefix <code>{f.allowedPrefix}</code> is not the one for country <code>{f.country}</code>
            {prefixForCountry(f.country) && <> (expected <code>{prefixForCountry(f.country)}</code>)</>}.
            Numbers that do not start with <code>{f.allowedPrefix}</code> will be rejected at import.
          </div>
        )}

        <div className="grid2">
          <label><span>Trunk</span>
            <select value={f.trunkId} onChange={set('trunkId')}>
              {trunks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></label>
          <label><span>Presented CLI (without +)</span>
            <select value={f.cli} onChange={set('cli')}>
              {(trunk?.dids ?? []).map((d) => <option key={d}>{d}</option>)}
            </select></label>
        </div>

        <label><span>Message</span>
          <select value={f.ttsMessageId} onChange={set('ttsMessageId')} required>
            <option value={0}>— choose —</option>
            {messages.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · {m.lang} · {((m.durationMs ?? 0) / 1000).toFixed(1)}s
              </option>
            ))}
          </select></label>

        <label><span>Destination rate (EUR / minute)</span>
          <input type="number" step="0.0001" value={f.costPerMin} onChange={set('costPerMin')} /></label>
        <p className="muted" style={{ marginTop: -6, fontSize: 12 }}>
          Billing starts only at the answer — ringing costs nothing. With this set, the
          campaign screen shows how much has been spent and how much is still estimated
          before you press Start.
        </p>

        <div className="grid3">
          <label><span>Concurrency</span><input type="number" value={f.concurrency} onChange={set('concurrency')} /></label>
          <label><span>Calls / minute</span><input type="number" value={f.callsPerMin} onChange={set('callsPerMin')} /></label>
          <label><span>Ring timeout (s)</span><input type="number" value={f.ringTimeout} onChange={set('ringTimeout')} /></label>
        </div>

        {!editId && (
          <label><span>Numbers file (CSV or a single column)</span>
            <input type="file" accept=".csv,.txt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
        )}

        <div className="row">
          <button className="primary" disabled={busy || !Number(f.ttsMessageId)}>
            <Icon name={editId ? 'clone' : 'plus'} />
            {busy ? 'Saving…' : editId ? 'Save' : 'Create campaign'}
          </button>
          {editId && <button type="button" onClick={() => nav(`/campaigns/${editId}`)}>Cancel</button>}
        </div>
      </form>
    </>
  );
}
